import { AccessCard } from '@/components/cards/AccessCard';
import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFormContext } from '@/components/react-hook-form';
import { ClientsTypeahead } from '@/modules/household/components/ClientsTypeahead/ClientsTypeahead';

import { TeamMembersTypeahead } from './TeamMembersTypeahead';
import { TeamFormPaths, TeamFormShape } from './TeamModal.types';

interface TeamFormProps {
  disabled?: boolean;
  scimManaged?: boolean;
}

export function TeamForm({ disabled, scimManaged }: TeamFormProps) {
  const { control } = useFormContext<TeamFormShape>();

  return (
    <FormFieldsDisabledProvider isDisabled={disabled ?? false}>
      {scimManaged && (
        <Callout severity="info-high" sx={{ mb: 3 }}>
          This team is managed by SCIM. Name and member changes must be made in
          your identity provider and will sync automatically. You can still
          manage client assignments here.
        </Callout>
      )}
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareTextInput<TeamFormShape>
            control={control}
            label="Name"
            fieldName={'name' as const satisfies TeamFormPaths}
            required
            disabled={scimManaged}
          />
        </FormLayoutItem>
      </FormLayoutRow>

      <FormLayoutRow>
        <FormLayoutItem>
          <TeamMembersTypeahead<TeamFormShape>
            control={control}
            label="Members"
            fieldName={'employeeIds' as const satisfies TeamFormPaths}
            disabled={scimManaged}
          />
        </FormLayoutItem>
      </FormLayoutRow>

      <AccessCard
        heading="Client access"
        subheading="Select the clients to which members of this team have access"
      >
        <FormLayoutRow>
          <FormLayoutItem width={12}>
            <ClientsTypeahead<TeamFormShape>
              control={control}
              label="Individual clients"
              fieldName={
                'assignedHouseholdIds' as const satisfies TeamFormPaths
              }
            />
          </FormLayoutItem>
        </FormLayoutRow>
      </AccessCard>
    </FormFieldsDisabledProvider>
  );
}
